/**
 * Pooled visual effects: cube-gib bursts (tanks shatter into little cubes when
 * destroyed — mirrors the zombies game's corpse debris, flying away along the
 * shot direction) and mine detonations (a bright additive flash + an expanding
 * shockwave ring + an outward debris shower).
 *
 * The cube debris is a single InstancedMesh (one draw call + one shadow draw for
 * the whole pool, even during a big multi-kill); rings and flashes are tiny pools
 * of plain meshes. Everything is pre-allocated and recycled — no per-frame
 * allocation — updated once per frame and cleared between floors. Numbers are
 * tuned for STUD scale (tanks are ~20 wide): the zombies recipe scaled up ~14×.
 */

import * as THREE from "three";

const GIB_GEOM = new THREE.BoxGeometry(3.4, 3.4, 3.4);
const RING_GEOM = new THREE.RingGeometry(0.72, 1.0, 40);
const FLASH_GEOM = new THREE.SphereGeometry(1, 12, 12);
const AURA_GEOM = new THREE.SphereGeometry(1, 24, 16); // smooth shield bubble
// Fresnel rim shader: transparent in the middle, glowing at the edges facing away
// from the camera — reads as a translucent energy shell, not a solid ball.
const AURA_VERT = `
  varying vec3 vN; varying vec3 vV;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vN = normalize(mat3(modelMatrix) * normal);
    vV = normalize(cameraPosition - wp.xyz);
    gl_Position = projectionMatrix * viewMatrix * wp;
  }`;
const AURA_FRAG = `
  uniform vec3 uColor; uniform float uOpacity;
  varying vec3 vN; varying vec3 vV;
  void main() {
    float fres = pow(1.0 - abs(dot(normalize(vN), normalize(vV))), 2.2);
    gl_FragColor = vec4(uColor, fres * uOpacity);
  }`;
const GRAVITY = 380; // studs/s² (scaled from zombies' 22)

// scratch (no per-frame allocation)
const _mat = new THREE.Matrix4();
const _euler = new THREE.Euler();
const _quat = new THREE.Quaternion();
const _pos = new THREE.Vector3();
const _scl = new THREE.Vector3();
const _col = new THREE.Color();
const HIDDEN = new THREE.Matrix4().makeScale(0, 0, 0);

interface Cube { x: number; y: number; z: number; rx: number; ry: number; rz: number; s: number; vx: number; vy: number; vz: number; sx: number; sz: number; life: number; floorY: number }
interface Ring { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; life: number; max: number; radius: number }
interface Flash { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; life: number; max: number; radius: number }
interface Aura { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; life: number; max: number; r0: number; r1: number }

export class Effects {
  private cubes: Cube[] = [];
  private cubeMesh: THREE.InstancedMesh;
  private cNext = 0;
  private readonly CUBES = 320; // headroom for rocket trails + simultaneous explosions
  private cubesDirty = false;

  private rings: Ring[] = [];
  private rNext = 0;
  private readonly RINGS = 10;
  private flashes: Flash[] = [];
  private fNext = 0;
  private readonly FLASHES = 10;
  private auras: Aura[] = [];
  private aNext = 0;
  private readonly AURAS = 8; // shield flares (player + many enemies can pop at once)
  private mflashes: Flash[] = [];
  private mfNext = 0;
  private readonly MFLASHES = 16; // DEDICATED muzzle flashes — so gatling rapid-fire can't tear the mine/spark flashes

  constructor(scene: THREE.Scene) {
    const gibMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
    this.cubeMesh = new THREE.InstancedMesh(GIB_GEOM, gibMat, this.CUBES);
    this.cubeMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.cubeMesh.castShadow = true;
    this.cubeMesh.frustumCulled = false;
    for (let i = 0; i < this.CUBES; i++) {
      this.cubes.push({ x: 0, y: 0, z: 0, rx: 0, ry: 0, rz: 0, s: 1, vx: 0, vy: 0, vz: 0, sx: 0, sz: 0, life: 0, floorY: 0 });
      this.cubeMesh.setMatrixAt(i, HIDDEN);
      this.cubeMesh.setColorAt(i, _col.setHex(0xffffff));
    }
    this.cubeMesh.instanceMatrix.needsUpdate = true;
    if (this.cubeMesh.instanceColor) this.cubeMesh.instanceColor.needsUpdate = true;
    scene.add(this.cubeMesh);

    for (let i = 0; i < this.RINGS; i++) {
      const mat = new THREE.MeshBasicMaterial({ color: 0xffdd88, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
      const mesh = new THREE.Mesh(RING_GEOM, mat);
      mesh.rotation.x = -Math.PI / 2; mesh.visible = false;
      scene.add(mesh);
      this.rings.push({ mesh, mat, life: 0, max: 1, radius: 1 });
    }
    for (let i = 0; i < this.FLASHES; i++) {
      const mat = new THREE.MeshBasicMaterial({ color: 0xffd27a, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending });
      const mesh = new THREE.Mesh(FLASH_GEOM, mat);
      mesh.visible = false;
      scene.add(mesh);
      this.flashes.push({ mesh, mat, life: 0, max: 1, radius: 1 });
    }
    for (let i = 0; i < this.AURAS; i++) {
      const mat = new THREE.ShaderMaterial({
        uniforms: { uColor: { value: new THREE.Color(0x3aa8ff) }, uOpacity: { value: 0 } },
        vertexShader: AURA_VERT, fragmentShader: AURA_FRAG,
        transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
      });
      const mesh = new THREE.Mesh(AURA_GEOM, mat);
      mesh.visible = false;
      scene.add(mesh);
      this.auras.push({ mesh, mat, life: 0, max: 1, r0: 1, r1: 1 });
    }
    for (let i = 0; i < this.MFLASHES; i++) {
      const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending });
      const mesh = new THREE.Mesh(FLASH_GEOM, mat);
      mesh.visible = false;
      scene.add(mesh);
      this.mflashes.push({ mesh, mat, life: 0, max: 1, radius: 1 });
    }
  }

  private writeCube(i: number): void {
    const c = this.cubes[i];
    if (c.life <= 0) { this.cubeMesh.setMatrixAt(i, HIDDEN); return; }
    _euler.set(c.rx, c.ry, c.rz);
    _quat.setFromEuler(_euler);
    _pos.set(c.x, c.y, c.z);
    _scl.set(c.s, c.s, c.s);
    this.cubeMesh.setMatrixAt(i, _mat.compose(_pos, _quat, _scl));
  }

  private spawnCube(color: number): Cube {
    const i = this.cNext;
    this.cNext = (this.cNext + 1) % this.CUBES;
    this.cubeMesh.setColorAt(i, _col.setHex(color));
    if (this.cubeMesh.instanceColor) this.cubeMesh.instanceColor.needsUpdate = true;
    return this.cubes[i];
  }

  /**
   * Burst `count` cubes at (x,y,z) flying away along (dirx,dirz) — the shot
   * direction (so a destroyed tank's pieces scatter in the way it was hit). With
   * no direction (mine kill) they spray radially. `floorY` is the rest height.
   */
  gib(x: number, y: number, z: number, floorY: number, dirx: number, dirz: number, color: number, count = 16): void {
    const dl = Math.hypot(dirx, dirz);
    for (let i = 0; i < count; i++) {
      const c = this.spawnCube(color);
      c.x = x + (Math.random() - 0.5) * 12; c.y = y + Math.random() * 10; c.z = z + (Math.random() - 0.5) * 12;
      c.rx = Math.random() * 6; c.ry = Math.random() * 6; c.rz = Math.random() * 6;
      c.s = 0.7 + Math.random() * 0.7;
      let bx: number, bz: number;
      if (dl > 0.01) { bx = dirx / dl; bz = dirz / dl; }
      else { const a = Math.random() * Math.PI * 2; bx = Math.cos(a); bz = Math.sin(a); }
      const spread = (Math.random() - 0.5) * 1.4; // fan out around the launch dir
      const cs = Math.cos(spread), sn = Math.sin(spread);
      const spd = 70 + Math.random() * 90;
      c.vx = (bx * cs - bz * sn) * spd;
      c.vz = (bx * sn + bz * cs) * spd;
      c.vy = 70 + Math.random() * 80;
      c.sx = (Math.random() - 0.5) * 16;
      c.sz = (Math.random() - 0.5) * 14;
      c.life = 1.0 + Math.random() * 0.5;
      c.floorY = floorY;
    }
    this.cubesDirty = true;
  }

  /** A satisfying mine detonation: a flash, an expanding shockwave ring on the
   *  ground plane (sized to the blast radius), and an outward debris shower. */
  mineBlast(x: number, floorY: number, z: number, radius: number): void {
    const f = this.flashes[this.fNext]; this.fNext = (this.fNext + 1) % this.FLASHES;
    f.mesh.position.set(x, floorY + 8, z);
    f.mat.color.setHex(0xffe8a0); f.mat.opacity = 0.95;
    f.mesh.scale.setScalar(radius * 0.4); f.mesh.visible = true;
    f.life = 0.22; f.max = 0.22; f.radius = radius * 1.25;

    const r = this.rings[this.rNext]; this.rNext = (this.rNext + 1) % this.RINGS;
    r.mesh.position.set(x, floorY + 1.0, z);
    r.mat.color.setHex(0xffdd88); r.mat.opacity = 0.9;
    r.mesh.scale.setScalar(radius * 0.2); r.mesh.visible = true;
    r.life = 0.5; r.max = 0.5; r.radius = radius * 1.7;

    for (let i = 0; i < 14; i++) {
      const c = this.spawnCube(Math.random() < 0.5 ? 0x3a3d42 : 0xff7a2a); // shrapnel + sparks
      c.x = x + (Math.random() - 0.5) * 10; c.y = floorY + 4 + Math.random() * 8; c.z = z + (Math.random() - 0.5) * 10;
      c.rx = Math.random() * 6; c.ry = Math.random() * 6; c.rz = Math.random() * 6;
      c.s = 0.5 + Math.random() * 0.6;
      const a = Math.random() * Math.PI * 2, el = 0.1 + Math.random() * 0.6;
      const spd = 80 + Math.random() * 100;
      c.vx = Math.cos(a) * Math.cos(el) * spd;
      c.vz = Math.sin(a) * Math.cos(el) * spd;
      c.vy = Math.sin(el) * spd + 60;
      c.sx = (Math.random() - 0.5) * 18;
      c.sz = (Math.random() - 0.5) * 16;
      c.life = 0.9 + Math.random() * 0.5;
      c.floorY = floorY;
    }
    this.cubesDirty = true;
  }

  /** A small spark burst when a shell ricochets — a few bright cubes flung back
   *  along the reflected velocity + a tiny flash. Cheap; reuses the pools. */
  spark(x: number, y: number, z: number, velx: number, velz: number, floorY: number): void {
    const vl = Math.hypot(velx, velz) || 1;
    const bx = velx / vl, bz = velz / vl;
    const n = 4 + ((Math.random() * 3) | 0);
    for (let i = 0; i < n; i++) {
      const c = this.spawnCube(0xffffcc);
      c.x = x + (Math.random() - 0.5) * 4; c.y = y + (Math.random() - 0.5) * 3; c.z = z + (Math.random() - 0.5) * 4;
      c.rx = Math.random() * 6; c.ry = Math.random() * 6; c.rz = Math.random() * 6;
      c.s = 0.18 + Math.random() * 0.22;
      const spread = (Math.random() - 0.5) * 0.7;
      const cs = Math.cos(spread), sn = Math.sin(spread);
      const spd = 55 + Math.random() * 55;
      c.vx = (bx * cs - bz * sn) * spd;
      c.vz = (bx * sn + bz * cs) * spd;
      c.vy = 25 + Math.random() * 35;
      c.sx = (Math.random() - 0.5) * 10; c.sz = (Math.random() - 0.5) * 10;
      c.life = 0.22 + Math.random() * 0.12;
      c.floorY = floorY;
    }
    this.cubesDirty = true;
    const f = this.flashes[this.fNext]; this.fNext = (this.fNext + 1) % this.FLASHES;
    f.mesh.position.set(x, y, z);
    f.mat.color.setHex(0xffffaa); f.mat.opacity = 0.6;
    f.mesh.scale.setScalar(4); f.mesh.visible = true;
    f.life = 0.13; f.max = 0.13; f.radius = 7;
  }

  /** A cool blue shield flare when a tank's armour soaks a hit (player + enemies):
   *  a translucent energy bubble blooms around the tank, a ground shockwave ring,
   *  and a sprinkle of blue sparks. (x,y,z) is the tank centre. Reuses the pools. */
  shieldPop(x: number, y: number, z: number, floorY: number): void {
    const a = this.auras[this.aNext]; this.aNext = (this.aNext + 1) % this.AURAS;
    a.mesh.position.set(x, y, z);
    a.mesh.scale.setScalar(11); a.mesh.visible = true;
    a.life = 0.5; a.max = 0.5; a.r0 = 11; a.r1 = 21; // blooms from tank-hugging outward

    const r = this.rings[this.rNext]; this.rNext = (this.rNext + 1) % this.RINGS;
    r.mesh.position.set(x, floorY + 1.5, z);
    r.mat.color.setHex(0x7fe0ff); r.mat.opacity = 0.85;
    r.mesh.scale.setScalar(6); r.mesh.visible = true;
    r.life = 0.45; r.max = 0.45; r.radius = 24;

    for (let i = 0; i < 8; i++) {
      const c = this.spawnCube(Math.random() < 0.5 ? 0x9fe6ff : 0x3aa0ff);
      const ang = (i / 8) * Math.PI * 2 + Math.random() * 0.4;
      c.x = x + Math.cos(ang) * 6; c.y = y + (Math.random() - 0.5) * 6; c.z = z + Math.sin(ang) * 6;
      c.rx = Math.random() * 6; c.ry = Math.random() * 6; c.rz = Math.random() * 6;
      c.s = 0.25 + Math.random() * 0.3;
      const spd = 45 + Math.random() * 45;
      c.vx = Math.cos(ang) * spd; c.vz = Math.sin(ang) * spd; c.vy = 30 + Math.random() * 40;
      c.sx = (Math.random() - 0.5) * 10; c.sz = (Math.random() - 0.5) * 10;
      c.life = 0.3 + Math.random() * 0.2;
      c.floorY = floorY;
    }
    this.cubesDirty = true;
  }

  /** One faint smoke/spark puff behind a rocket (very cheap — a single cube). */
  trail(x: number, y: number, z: number, floorY: number): void {
    const c = this.spawnCube(Math.random() < 0.5 ? 0x6b6b6b : 0xff9a3a);
    c.x = x + (Math.random() - 0.5) * 3; c.y = y + (Math.random() - 0.5) * 3; c.z = z + (Math.random() - 0.5) * 3;
    c.rx = Math.random() * 6; c.ry = Math.random() * 6; c.rz = Math.random() * 6;
    c.s = 0.4 + Math.random() * 0.3;
    c.vx = (Math.random() - 0.5) * 14; c.vz = (Math.random() - 0.5) * 14; c.vy = 6 + Math.random() * 10;
    c.sx = (Math.random() - 0.5) * 6; c.sz = (Math.random() - 0.5) * 6;
    c.life = 0.25 + Math.random() * 0.15;
    c.floorY = floorY;
    this.cubesDirty = true;
  }

  /** A muzzle flash at the barrel tip (x,y,z) when a tank fires — color/size/duration set by the caller
   *  per weapon type. `sparks` adds a few bright cubes flung along (dirx,dirz); 0 for rapid fire. Uses a
   *  DEDICATED flash pool so gatling can't tear the mine/spark flashes. */
  muzzleFlash(x: number, y: number, z: number, color: number, radius: number, life: number, sparks: number, dirx: number, dirz: number, floorY: number): void {
    const f = this.mflashes[this.mfNext]; this.mfNext = (this.mfNext + 1) % this.MFLASHES;
    f.mesh.position.set(x, y, z);
    f.mat.color.setHex(color); f.mat.opacity = 0.9;
    f.mesh.scale.setScalar(radius * 0.5); f.mesh.visible = true;
    f.life = life; f.max = life; f.radius = radius;
    if (sparks > 0) {
      const dl = Math.hypot(dirx, dirz) || 1, bx = dirx / dl, bz = dirz / dl;
      for (let i = 0; i < sparks; i++) {
        const c = this.spawnCube(color);
        c.x = x + (Math.random() - 0.5) * 3; c.y = y + (Math.random() - 0.5) * 3; c.z = z + (Math.random() - 0.5) * 3;
        c.rx = Math.random() * 6; c.ry = Math.random() * 6; c.rz = Math.random() * 6;
        c.s = 0.2 + Math.random() * 0.28;
        const spread = (Math.random() - 0.5) * 0.8, cs = Math.cos(spread), sn = Math.sin(spread), spd = 60 + Math.random() * 70;
        c.vx = (bx * cs - bz * sn) * spd; c.vz = (bx * sn + bz * cs) * spd; c.vy = 18 + Math.random() * 28;
        c.sx = (Math.random() - 0.5) * 10; c.sz = (Math.random() - 0.5) * 10;
        c.life = 0.14 + Math.random() * 0.1; c.floorY = floorY;
      }
      this.cubesDirty = true;
    }
  }

  update(dt: number): void {
    let dirty = this.cubesDirty;
    this.cubesDirty = false;
    for (let i = 0; i < this.CUBES; i++) {
      const c = this.cubes[i];
      if (c.life <= 0) continue;
      c.life -= dt;
      c.vy -= GRAVITY * dt;
      c.x += c.vx * dt; c.y += c.vy * dt; c.z += c.vz * dt;
      c.rx += c.sx * dt; c.rz += c.sz * dt;
      const rest = c.floorY + 2;
      if (c.y < rest) { c.y = rest; c.vy = -c.vy * 0.32; c.vx *= 0.6; c.vz *= 0.6; }
      this.writeCube(i);
      dirty = true;
    }
    if (dirty) this.cubeMesh.instanceMatrix.needsUpdate = true;

    for (const r of this.rings) {
      if (r.life <= 0) continue;
      r.life -= dt;
      const t = 1 - r.life / r.max;
      const eased = 1 - Math.pow(1 - t, 3);
      r.mesh.scale.setScalar(r.radius * (0.2 + eased));
      r.mat.opacity = 0.9 * (1 - t);
      if (r.life <= 0) r.mesh.visible = false;
    }
    for (const f of this.flashes) {
      if (f.life <= 0) continue;
      f.life -= dt;
      const t = 1 - f.life / f.max;
      f.mesh.scale.setScalar(f.radius * (0.4 + t * 0.9));
      f.mat.opacity = 0.95 * (1 - t);
      if (f.life <= 0) f.mesh.visible = false;
    }
    for (const f of this.mflashes) {
      if (f.life <= 0) continue;
      f.life -= dt;
      const t = 1 - f.life / f.max;
      f.mesh.scale.setScalar(f.radius * (0.5 + t * 0.8));
      f.mat.opacity = 0.9 * (1 - t);
      if (f.life <= 0) f.mesh.visible = false;
    }
    for (const a of this.auras) {
      if (a.life <= 0) continue;
      a.life -= dt;
      const t = 1 - a.life / a.max;
      const eased = 1 - Math.pow(1 - t, 2); // ease-out swell
      a.mesh.scale.setScalar(a.r0 + (a.r1 - a.r0) * eased);
      a.mat.uniforms.uOpacity.value = 0.95 * Math.sin(Math.min(1, t) * Math.PI); // bloom in, fade out
      if (a.life <= 0) a.mesh.visible = false;
    }
  }

  /** Hide everything (between floors / on game over). */
  clear(): void {
    for (let i = 0; i < this.CUBES; i++) { this.cubes[i].life = 0; this.cubeMesh.setMatrixAt(i, HIDDEN); }
    this.cubeMesh.instanceMatrix.needsUpdate = true;
    for (const r of this.rings) { r.life = 0; r.mesh.visible = false; }
    for (const f of this.flashes) { f.life = 0; f.mesh.visible = false; }
    for (const f of this.mflashes) { f.life = 0; f.mesh.visible = false; }
    for (const a of this.auras) { a.life = 0; a.mesh.visible = false; }
  }
}
